import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getHotPotatoOptions, sendHotPotato, hotPotatoGraphicUrl } from '../api/hotPotato';
import { HotPotatoIcon } from '../assets/icons';
import '../styles/HotPotato.css';

const sameName = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();

const HotPotatoSendPage = () => {
    const history = useHistory();
    const location = useLocation();
    const presetUsername = useMemo(
        () => new URLSearchParams(location.search).get('to') || '',
        [location.search]
    );

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [messages, setMessages] = useState([]);
    const [graphics, setGraphics] = useState([]);
    const [friends, setFriends] = useState([]);
    const [baseCost, setBaseCost] = useState(0);
    const [balance, setBalance] = useState(0);

    const [recipientId, setRecipientId] = useState('');
    const [pickingFriend, setPickingFriend] = useState(false);
    const [messageId, setMessageId] = useState('');
    const [graphicId, setGraphicId] = useState('');

    const [confirming, setConfirming] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [sent, setSent] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await getHotPotatoOptions();
                if (!mounted) return;
                const friendList = result.friends || [];
                setMessages(result.messages || []);
                setGraphics(result.graphics || []);
                setFriends(friendList);
                setBaseCost(result.cost_spudcoin ?? 0);
                setBalance(result.spudcoin_balance ?? 0);

                if (presetUsername) {
                    const match = friendList.find((f) => sameName(f.username, presetUsername));
                    if (match) {
                        setRecipientId(match.friend_id);
                    } else {
                        setPickingFriend(true);
                        setSendError(`${presetUsername} isn't on your friends list. Hot Potatoes can only go to friends.`);
                    }
                } else {
                    setPickingFriend(true);
                }
            } catch (err) {
                if (mounted) setError(err.message || 'Failed to load Hot Potato options');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [presetUsername]);

    const recipient = friends.find((f) => f.friend_id === recipientId) || null;
    const message = messages.find((m) => m.id === messageId) || null;
    const graphic = graphics.find((g) => g.id === graphicId) || null;
    const totalCost = baseCost + (message?.price_spudcoin || 0) + (graphic?.price_spudcoin || 0);
    const affordable = balance >= totalCost;
    const ready = Boolean(recipient && message && graphic);

    const handleSend = async () => {
        if (!ready) return;
        setSending(true);
        setSendError(null);
        try {
            const result = await sendHotPotato({
                recipient_id: recipient.friend_id,
                message_id: message.id,
                graphic_id: graphic.id,
            });
            setBalance(result.spudcoin_balance ?? balance);
            setSent({ id: result.id, recipient, message, graphic, spent: result.spudcoin_spent });
            setConfirming(false);
        } catch (err) {
            if (err.spudcoin_balance != null) setBalance(err.spudcoin_balance);
            setSendError(err.message || 'Failed to send Hot Potato');
            setConfirming(false);
        } finally {
            setSending(false);
        }
    };

    const resetForm = () => {
        setSent(null);
        setMessageId('');
        setGraphicId('');
        setSendError(null);
    };

    const renderShell = (children) => (
        <div className="sc-page hp-page">
            <div className="sc-container">
                <header className="sc-header hp-header">
                    <button type="button" className="sc-back-link" onClick={() => history.goBack()}>
                        ← Back
                    </button>
                    <h2>
                        <HotPotatoIcon aria-hidden="true" className="hp-title-icon" />
                        Send Hot Potato
                    </h2>
                    <div className="hp-balance" aria-label={`${balance} SpudCoin`}>
                        <span className="hp-balance-icon">🪙</span>
                        <span className="hp-balance-value">{balance}</span>
                    </div>
                </header>
                {children}
            </div>
        </div>
    );

    if (loading) {
        return renderShell(
            <div className="sc-loading">
                <div className="sc-spinner" />
                <p>Warming up the oven…</p>
            </div>
        );
    }

    if (error) {
        return renderShell(
            <div className="hp-body">
                <div className="sc-alert sc-alert--error" role="alert">{error}</div>
            </div>
        );
    }

    if (sent) {
        return renderShell(
            <div className="hp-body hp-sent">
                <div className="hp-preview hp-preview--sent">
                    <img
                        className="hp-preview-graphic"
                        src={hotPotatoGraphicUrl(sent.graphic.payload)}
                        alt={sent.graphic.label}
                    />
                    <p className="hp-preview-message">{sent.message.payload}</p>
                    <p className="hp-preview-to">To <strong>{sent.recipient.username}</strong></p>
                </div>
                <div className="sc-alert sc-alert--success" role="status">
                    🔥 Hot Potato sent! 🪙 {sent.spent} SpudCoin spent — you have 🪙 {balance} left.
                </div>
                <div className="hp-actions">
                    <button type="button" className="sc-btn sc-btn--primary" onClick={() => history.push(`/hot-potato/${sent.id}`)}>
                        View it
                    </button>
                    <button type="button" className="sc-btn sc-btn--secondary" onClick={resetForm}>
                        Send another
                    </button>
                    <button type="button" className="sc-btn sc-btn--secondary" onClick={() => history.push('/hot-potato')}>
                        My Hot Potatoes
                    </button>
                </div>
            </div>
        );
    }

    return renderShell(
        <div className="hp-body">
            {sendError && <div className="sc-alert sc-alert--error" role="alert">{sendError}</div>}

            {/* Recipient */}
            <section className="hp-section" aria-labelledby="hp-to-label">
                <div className="hp-section-head">
                    <p id="hp-to-label" className="hp-section-title">To</p>
                    {recipient && !pickingFriend && (
                        <button type="button" className="hp-link-btn" onClick={() => setPickingFriend(true)}>
                            Change
                        </button>
                    )}
                </div>
                {recipient && !pickingFriend ? (
                    <p className="hp-recipient">{recipient.username}</p>
                ) : friends.length === 0 ? (
                    <p className="hp-empty">
                        You don't have any friends yet. <button type="button" className="hp-link-btn" onClick={() => history.push('/friends')}>Find some</button> to send a Hot Potato.
                    </p>
                ) : (
                    <select
                        className="hp-select"
                        aria-labelledby="hp-to-label"
                        value={recipientId}
                        onChange={(e) => {
                            setRecipientId(e.target.value);
                            setSendError(null);
                            if (e.target.value) setPickingFriend(false);
                        }}
                    >
                        <option value="">Choose a friend…</option>
                        {friends.map((f) => (
                            <option key={f.friend_id} value={f.friend_id}>{f.username}</option>
                        ))}
                    </select>
                )}
            </section>

            {/* Message */}
            <section className="hp-section" aria-labelledby="hp-msg-label">
                <p id="hp-msg-label" className="hp-section-title">Pick a message</p>
                <div className="hp-choices" role="radiogroup" aria-labelledby="hp-msg-label">
                    {messages.map((m) => {
                        const active = m.id === messageId;
                        return (
                            <button
                                key={m.id}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                className={`hp-choice ${active ? 'is-active' : ''}`}
                                onClick={() => setMessageId(m.id)}
                            >
                                <span className="hp-choice-text">{m.payload}</span>
                                {m.price_spudcoin > 0 && <span className="hp-price">🪙 +{m.price_spudcoin}</span>}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Graphic */}
            <section className="hp-section" aria-labelledby="hp-gfx-label">
                <p id="hp-gfx-label" className="hp-section-title">Pick a graphic</p>
                <div className="hp-graphics" role="radiogroup" aria-labelledby="hp-gfx-label">
                    {graphics.map((g) => {
                        const active = g.id === graphicId;
                        return (
                            <button
                                key={g.id}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                aria-label={g.label}
                                className={`hp-graphic ${active ? 'is-active' : ''}`}
                                onClick={() => setGraphicId(g.id)}
                            >
                                <img src={hotPotatoGraphicUrl(g.payload)} alt="" />
                                <span className="hp-graphic-label">{g.label}</span>
                                {g.price_spudcoin > 0 && <span className="hp-price">🪙 +{g.price_spudcoin}</span>}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Preview */}
            <section className="hp-section" aria-labelledby="hp-preview-label">
                <p id="hp-preview-label" className="hp-section-title">Preview</p>
                <div className={`hp-preview ${ready ? '' : 'hp-preview--empty'}`}>
                    {graphic ? (
                        <img className="hp-preview-graphic" src={hotPotatoGraphicUrl(graphic.payload)} alt={graphic.label} />
                    ) : (
                        <div className="hp-preview-placeholder" aria-hidden="true">🥔</div>
                    )}
                    <p className="hp-preview-message">
                        {message ? message.payload : 'Your message will show up here.'}
                    </p>
                    <p className="hp-preview-to">
                        To <strong>{recipient ? recipient.username : '…'}</strong>
                    </p>
                </div>
            </section>

            <div className="hp-footer">
                <div className="hp-cost">
                    <span className="hp-price hp-price--lg">🪙 {totalCost}</span>
                    <span className={`hp-cost-note ${affordable ? '' : 'hp-cost-note--short'}`}>
                        {affordable
                            ? `You'll have 🪙 ${balance - totalCost} left.`
                            : `You need 🪙 ${totalCost - balance} more SpudCoin.`}
                    </span>
                </div>
                <button
                    type="button"
                    className="sc-btn sc-btn--primary"
                    disabled={!ready || !affordable}
                    onClick={() => { setSendError(null); setConfirming(true); }}
                >
                    <HotPotatoIcon aria-hidden="true" />
                    Send Hot Potato
                </button>
            </div>

            {confirming && (
                <div className="sc-overlay" onClick={() => !sending && setConfirming(false)}>
                    <div className="sc-modal hp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="hp-confirm-title">
                        <h3 id="hp-confirm-title" className="hp-modal-title">Send this Hot Potato?</h3>
                        <p className="hp-modal-body">
                            Spend <strong>🪙 {totalCost} SpudCoin</strong> to send it to{' '}
                            <strong>{recipient?.username}</strong>?
                        </p>
                        <p className="hp-modal-note">
                            You'll have 🪙 {balance - totalCost} left. Nothing is charged if the send fails.
                        </p>
                        <div className="hp-modal-actions">
                            <button type="button" className="sc-btn sc-btn--secondary" onClick={() => setConfirming(false)} disabled={sending}>
                                Cancel
                            </button>
                            <button type="button" className="sc-btn sc-btn--primary" onClick={handleSend} disabled={sending}>
                                {sending ? 'Sending…' : 'Yes, send it'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HotPotatoSendPage;
